import { Platform, Share } from 'react-native';

import { shareMessage } from './inviteFriendsCatalog';

const copyWithSelection = (text: string): boolean => {
	try {
		const field = document.createElement('textarea');
		field.value = text;
		field.setAttribute('readonly', '');
		field.style.position = 'fixed';
		field.style.opacity = '0';
		document.body.appendChild(field);
		field.select();
		const copied = document.execCommand('copy');
		document.body.removeChild(field);
		return copied;
	} catch {
		return false;
	}
};

/**
 * Copies to the clipboard - the async Clipboard API on web, `@react-native-clipboard` on native.
 * Same two paths `Sidebar`'s version footer uses. Resolves false when neither is available.
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
	if (Platform.OS === 'web') {
		try {
			await navigator.clipboard.writeText(text);
			return true;
		} catch {
			// `navigator.clipboard` only exists on secure origins (e.g. not a phone hitting the dev
			// server over LAN http), so fall back to the legacy selection copy.
			return copyWithSelection(text);
		}
	}

	try {
		const mod = require('@react-native-clipboard/clipboard');
		const Clipboard = mod?.default ?? mod;
		Clipboard.setString(text);
		return true;
	} catch {
		return false;
	}
};

/**
 * Opens the operating system's own share sheet, so the player picks WhatsApp, Viber, Messenger,
 * SMS or anything else installed - the spec deliberately integrates no network directly. Web uses
 * the Web Share API where the browser has one and falls back to copying the link.
 *
 * Resolves `'shared'`, `'copied'` (the web fallback) or `'dismissed'`.
 */
export const shareInvite = async (target: string): Promise<'shared' | 'copied' | 'dismissed'> => {
	const message = shareMessage(target);

	if (Platform.OS === 'web') {
		const nav = navigator as Navigator & { share?: (data: ShareData) => Promise<void> };
		if (typeof nav.share === 'function') {
			try {
				await nav.share({ title: 'Svara', text: message });
				return 'shared';
			} catch {
				return 'dismissed';
			}
		}
		return (await copyToClipboard(target)) ? 'copied' : 'dismissed';
	}

	try {
		const result = await Share.share({ message });
		return result.action === Share.dismissedAction ? 'dismissed' : 'shared';
	} catch {
		return 'dismissed';
	}
};
